// Drawing Guess (Pictionary-style party game, 3–12 players) — pure rules.
//
// No I/O; imports only the shared types and the word bank, so it runs in the
// `games` edge function (Deno), the browser and node tests.
//
// Flow: each turn one drawer picks one of three secret words, then draws it on
// the easel while everyone else types guesses. The server checks every guess
// (case / space / punctuation-insensitive), tells only the guesser when they're
// close, scores by speed, reveals letters on the game clock and ends the turn
// when time is up or everyone has guessed. After every seated player has drawn
// once a round ends; after `rounds` rounds the highest score wins.
//
// Secrets: the word is NEVER in the public state while it's being drawn. It
// lives in the drawer's private row (choices, then the word), in the private
// row of players who already guessed it, and in a spare row nobody owns
// (SECRET_SEAT) that the server reads back on every step.
//
// The strokes themselves are not here at all: they are live, non-authoritative
// visuals sent over the world channel (src/components/games/drawing/strokes.ts).

import { RuleError, type Ctx, type GameDef, type Privs, type SeatResult } from './types.ts';
import { WORD_TIERS, type Difficulty } from './drawing-words.ts';
export { RuleError };

export const MAX_PLAYERS = 12;
export const MIN_PLAYERS = 3;
/** Spare private row (≥ maxPlayers → no owner, unreadable by clients). */
export const SECRET_SEAT = 99;
export const CHOOSE_MS = 15_000;
export const REVEAL_MS = 6_000;
export const DRAW_SECONDS = [60, 80, 100] as const;
export const ROUND_OPTIONS = [2, 3] as const;
export const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard', 'mixed'];
export const CHAT_MAX = 40;
export const GUESS_MAX_LEN = 40;
/** Drawer earns this per correct guesser. */
export const DRAWER_PTS = 50;

export interface DrawOptions { rounds: number; drawSec: number; difficulty: Difficulty }
export const DEFAULT_OPTIONS: DrawOptions = { rounds: 3, drawSec: 80, difficulty: 'mixed' };

export interface ChatLine {
  n: number;
  seat: number;
  name: string;
  /** 'guess' = a wrong guess (text shown), 'correct' = "Name guessed it!" (no text), 'sys' = table event. */
  kind: 'guess' | 'correct' | 'sys';
  text: string;
}

export interface DrawState {
  phase: 'choosing' | 'drawing' | 'reveal' | 'over';
  rounds: number;
  round: number;
  drawSec: number;
  difficulty: Difficulty;
  /** Turns so far (1-based) — clients key the canvas on it. */
  turnNo: number;
  drawer: number;
  /** Seats that already drew this round. */
  drawn: number[];
  /** Phase deadline (choosing / drawing / reveal), ms epoch. */
  endsAt: number;
  /** When drawing started (ms epoch); 0 while choosing. */
  drawStart: number;
  /** Public hint: one entry per character — '_' hidden letter, ' ' gap, or the revealed char. Empty while choosing. */
  hint: string[];
  /** Times at which the next letters are revealed (ascending; consumed). */
  hintTimes: number[];
  /** Correct guessers this turn, in order. */
  guessed: { seat: number; at: number; pts: number }[];
  /** Scores by seat (length MAX_PLAYERS). */
  scores: number[];
  /** Points earned this turn by seat (for the reveal). */
  turnPts: number[];
  chat: ChatLine[];
  chatN: number;
  /** The finished turn: shown in the reveal phase (word public only after the turn). */
  reveal: { word: string | null; drawer: number; drawerName: string; reason: 'time' | 'all' | 'left' } | null;
  winners: number[];
}

/** Drawer's private row: choices while choosing, then the word. Guessers: close hint / the word once guessed. */
export interface DrawPriv {
  choices?: string[];
  word?: string;
  guessed?: boolean;
  /** Last close guess (only the guesser sees "so close!"). */
  close?: string;
  closeN?: number;
}
interface Secret { word: string | null; used: string[] }

export type DrawMove =
  | { type: 'choose'; index: number }
  | { type: 'guess'; text: string };

// ---------------------------------------------------------------------------
// Guess matching
// ---------------------------------------------------------------------------

/** Lowercase, accents and everything but letters/digits removed. */
export function normalize(s: string): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}

/**
 * 'correct' — the word (ignoring case, spaces, punctuation), or a phrase with
 * the word as one of its words ("a cat" for cat).
 * 'close'   — one letter off (two for long words), or the word hidden inside
 * a longer guess ("cats", "catfish"); never shown to anyone but the guesser.
 * 'wrong'   — shown in the public chat.
 */
export function judgeGuess(guess: string, word: string): 'correct' | 'close' | 'wrong' {
  const g = normalize(guess), w = normalize(word);
  if (!g || !w) return 'wrong';
  if (g === w) return 'correct';
  const tokens = String(guess).split(/\s+/).map(normalize).filter(Boolean);
  // A multi-word answer must match as a whole; a one-word answer may stand in a phrase.
  if (!/[\s-]/.test(word.trim()) && tokens.includes(w)) return 'correct';
  if (g.includes(w)) return 'close';
  const d = levenshtein(g, w);
  if (d <= 1 && w.length >= 3) return 'close';
  if (d <= 2 && w.length >= 7) return 'close';
  return 'wrong';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const seated = (ctx: Ctx) => ctx.seats.map((s, i) => (s ? i : -1)).filter((i) => i >= 0);
const nameOf = (ctx: Ctx, seat: number) => ctx.seats[seat]?.name ?? `Player ${seat + 1}`;
const secretOf = (privs: Privs): Secret => {
  const s = privs[SECRET_SEAT];
  return s && typeof s === 'object' ? { word: typeof s.word === 'string' ? s.word : null, used: Array.isArray(s.used) ? s.used : [] } : { word: null, used: [] };
};

function pushChat(g: DrawState, line: Omit<ChatLine, 'n'>): DrawState {
  const n = g.chatN + 1;
  const chat = [...g.chat, { ...line, n }];
  return { ...g, chat: chat.length > CHAT_MAX ? chat.slice(chat.length - CHAT_MAX) : chat, chatN: n };
}

/** Three distinct words the table hasn't had yet (the bank refills when used up). */
export function pickChoices(difficulty: Difficulty, used: string[], rng: () => number): string[] {
  const tiers = difficulty === 'mixed' ? (['easy', 'medium', 'hard'] as const) : ([difficulty, difficulty, difficulty] as const);
  const out: string[] = [];
  for (const tier of tiers) {
    const bank = WORD_TIERS[tier];
    let pool = bank.filter((w) => !used.includes(w) && !out.includes(w));
    if (!pool.length) pool = bank.filter((w) => !out.includes(w));
    out.push(pool[Math.floor(rng() * pool.length)]);
  }
  return out;
}

/** Public hint for a word: '_' per letter/digit, other characters (space, hyphen) as they are. */
export function maskWord(word: string, revealed: number[] = []): string[] {
  return [...word].map((ch, i) => (/[a-z0-9]/i.test(ch) ? (revealed.includes(i) ? ch : '_') : ch === ' ' ? ' ' : ch));
}

/** How many letters get revealed during a turn. */
export const hintCount = (word: string) => Math.min(3, Math.floor(normalize(word).length / 3));

/** Points for a correct guess `elapsed` ms into a `drawMs` turn: 300 at once, falling to 50. */
export function guessPoints(elapsed: number, drawMs: number): number {
  const left = Math.max(0, Math.min(1, 1 - elapsed / drawMs));
  return Math.max(50, Math.round((50 + 250 * left) / 5) * 5);
}

/** Next drawer this round: the first seated seat after `after` (wrapping) that hasn't drawn. */
function nextDrawer(ctx: Ctx, drawn: number[], after: number): number | null {
  const n = ctx.seats.length;
  for (let k = 1; k <= n; k++) {
    const s = (after + k + n) % n;
    if (ctx.seats[s] && !drawn.includes(s)) return s;
  }
  return null;
}

/** Clear every seat's private row (turn change) — returns the privs patch. */
function clearSeatPrivs(privs: Privs, n: number): Privs {
  const out: Privs = {};
  for (let s = 0; s < n; s++) if (privs[s] != null) out[s] = null;
  return out;
}

/** Begin a turn for `drawer` (choosing phase). */
function beginTurn(g: DrawState, privs: Privs, ctx: Ctx, drawer: number, drawn: number[], round: number): { g: DrawState; privs: Privs } {
  const sec = secretOf(privs);
  const choices = pickChoices(g.difficulty, sec.used, ctx.rng);
  const next: DrawState = {
    ...g, phase: 'choosing', round, drawer, drawn: [...drawn, drawer], turnNo: g.turnNo + 1,
    endsAt: ctx.now + CHOOSE_MS, drawStart: 0, hint: [], hintTimes: [], guessed: [],
    turnPts: Array(g.scores.length).fill(0), reveal: null,
  };
  const p = clearSeatPrivs(privs, g.scores.length);
  p[drawer] = { choices } satisfies DrawPriv;
  p[SECRET_SEAT] = { word: null, used: sec.used } satisfies Secret;
  return { g: next, privs: p };
}

/** The drawer picked (or the clock picked for them): drawing starts. */
function startDrawing(g: DrawState, privs: Privs, ctx: Ctx, word: string): { g: DrawState; privs: Privs } {
  const sec = secretOf(privs);
  const drawMs = g.drawSec * 1000;
  const k = hintCount(word);
  // Letters appear between 30 % and 80 % of the turn.
  const hintTimes = Array.from({ length: k }, (_, i) => ctx.now + Math.round(drawMs * (k === 1 ? 0.55 : 0.3 + (0.5 * i) / (k - 1))));
  const used = [...sec.used, word].slice(-300);
  return {
    g: { ...g, phase: 'drawing', endsAt: ctx.now + drawMs, drawStart: ctx.now, hint: maskWord(word), hintTimes },
    privs: { [g.drawer]: { word } satisfies DrawPriv, [SECRET_SEAT]: { word, used } satisfies Secret },
  };
}

/** End the current turn (time, everyone guessed, drawer left): reveal the word. */
function endTurn(g: DrawState, privs: Privs, ctx: Ctx, reason: 'time' | 'all' | 'left', drawerName?: string): { g: DrawState; privs: Privs; note: string } {
  const sec = secretOf(privs);
  const word = g.phase === 'drawing' ? sec.word : null;
  const name = drawerName ?? nameOf(ctx, g.drawer);
  let next: DrawState = {
    ...g, phase: 'reveal', endsAt: ctx.now + REVEAL_MS, hintTimes: [],
    hint: word ? [...word] : g.hint,
    reveal: { word, drawer: g.drawer, drawerName: name, reason },
  };
  const why = reason === 'all' ? 'Everyone got it!' : reason === 'left' ? `${name} left` : "Time's up!";
  next = pushChat(next, { seat: -1, name: '', kind: 'sys', text: word ? `${why} The word was "${word}".` : `${why} Skipping this turn.` });
  const p = clearSeatPrivs(privs, g.scores.length);
  p[SECRET_SEAT] = { word: null, used: sec.used } satisfies Secret;
  return { g: next, privs: p, note: word ? `The word was "${word}"` : why };
}

/** After the reveal: the next drawer, the next round, or game over. */
function advance(g: DrawState, privs: Privs, ctx: Ctx): { g: DrawState; privs?: Privs; note?: string } {
  const present = seated(ctx);
  if (present.length < 2) return finish(g, privs, ctx);
  const drawn = g.drawn.filter((s) => ctx.seats[s]);
  const nxt = nextDrawer(ctx, drawn, g.drawer);
  if (nxt !== null) {
    const r = beginTurn(g, privs, ctx, nxt, drawn, g.round);
    return { ...r, note: `${nameOf(ctx, nxt)} is drawing` };
  }
  if (g.round >= g.rounds) return finish(g, privs, ctx);
  const first = nextDrawer(ctx, [], -1)!;
  const r = beginTurn(g, privs, ctx, first, [], g.round + 1);
  return { g: pushChat(r.g, { seat: -1, name: '', kind: 'sys', text: `Round ${g.round + 1} of ${g.rounds}` }), privs: r.privs, note: `Round ${g.round + 1}` };
}

function finish(g: DrawState, privs: Privs, ctx: Ctx): { g: DrawState; privs: Privs; note: string } {
  const present = seated(ctx);
  const best = present.length ? Math.max(...present.map((s) => g.scores[s] || 0)) : 0;
  const winners = present.filter((s) => (g.scores[s] || 0) === best);
  return {
    g: { ...g, phase: 'over', endsAt: 0, hintTimes: [], winners },
    privs: { ...clearSeatPrivs(privs, g.scores.length), [SECRET_SEAT]: null },
    note: winners.length ? `${winners.map((s) => nameOf(ctx, s)).join(' & ')} win${winners.length > 1 ? '' : 's'}!` : 'Game over',
  };
}

/** Every seated guesser (not the drawer) has guessed. */
function allGuessed(g: DrawState, ctx: Ctx): boolean {
  const guessers = seated(ctx).filter((s) => s !== g.drawer);
  return guessers.length > 0 && guessers.every((s) => g.guessed.some((x) => x.seat === s));
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export function configureOptions(cur: unknown, patch: unknown): DrawOptions {
  const base: DrawOptions = { ...DEFAULT_OPTIONS, ...(cur && typeof cur === 'object' ? (cur as Partial<DrawOptions>) : {}) };
  if (!patch || typeof patch !== 'object') throw new RuleError('Bad options');
  const p = patch as Record<string, unknown>;
  const out = { ...base };
  if (p.rounds !== undefined) {
    const r = Number(p.rounds);
    if (!(ROUND_OPTIONS as readonly number[]).includes(r)) throw new RuleError('Rounds must be 2 or 3');
    out.rounds = r;
  }
  if (p.drawSec !== undefined) {
    const s = Number(p.drawSec);
    if (!(DRAW_SECONDS as readonly number[]).includes(s)) throw new RuleError('Draw time must be 60, 80 or 100 seconds');
    out.drawSec = s;
  }
  if (p.difficulty !== undefined) {
    if (!DIFFICULTIES.includes(p.difficulty as Difficulty)) throw new RuleError('Unknown difficulty');
    out.difficulty = p.difficulty as Difficulty;
  }
  return out;
}

const optionsOf = (o: unknown): DrawOptions => {
  try { return configureOptions(null, o && typeof o === 'object' ? o : {}); } catch { return { ...DEFAULT_OPTIONS }; }
};

// ---------------------------------------------------------------------------
// The GameDef
// ---------------------------------------------------------------------------

export function newGame(ctx: Ctx): { g: DrawState; privs: Privs } {
  const o = optionsOf(ctx.options);
  const base: DrawState = {
    phase: 'choosing', rounds: o.rounds, round: 1, drawSec: o.drawSec, difficulty: o.difficulty,
    turnNo: 0, drawer: -1, drawn: [], endsAt: 0, drawStart: 0, hint: [], hintTimes: [], guessed: [],
    scores: Array(ctx.seats.length).fill(0), turnPts: Array(ctx.seats.length).fill(0),
    chat: [], chatN: 0, reveal: null, winners: [],
  };
  const first = nextDrawer(ctx, [], -1);
  if (first === null) throw new RuleError('Nobody is playing');
  const r = beginTurn(base, { [SECRET_SEAT]: { word: null, used: [] } }, ctx, first, [], 1);
  return { g: pushChat(r.g, { seat: -1, name: '', kind: 'sys', text: `Round 1 of ${o.rounds}` }), privs: r.privs };
}

export const drawingGame: GameDef<DrawState, DrawMove> = {
  kind: 'drawing',
  label: 'Drawing Guess',
  minPlayers: MIN_PLAYERS,
  maxPlayers: MAX_PLAYERS,
  joinMidGame: true,
  turnTimeoutMs: null,

  configure: configureOptions,

  start: (_prev, ctx) => newGame(ctx),

  // Everyone acts at once (guessing); the game clock drives the turns.
  turnSeat: () => null,
  canAct: (g, actor) => actor >= 0 && g.phase !== 'over',

  move: (g, privs, actor, m, ctx) => {
    if (!m || typeof m !== 'object') throw new RuleError('Bad move');
    if (m.type === 'choose') {
      if (g.phase !== 'choosing') throw new RuleError('Not choosing a word now');
      if (actor !== g.drawer) throw new RuleError('Only the drawer picks the word');
      const idx = Number(m.index);
      const choices = (privs[actor] as DrawPriv | null)?.choices ?? [];
      if (!Number.isInteger(idx) || idx < 0 || idx >= choices.length) throw new RuleError('Pick one of the three words');
      const r = startDrawing(g, privs, ctx, choices[idx]);
      return { ...r, note: `${nameOf(ctx, actor)} is drawing` };
    }
    if (m.type === 'guess') {
      if (g.phase !== 'drawing') throw new RuleError('No drawing to guess right now');
      if (actor === g.drawer) throw new RuleError("You're the one drawing!");
      if (g.guessed.some((x) => x.seat === actor)) throw new RuleError('You already guessed it');
      const text = String(m.text ?? '').replace(/\s+/g, ' ').trim().slice(0, GUESS_MAX_LEN);
      if (!normalize(text)) throw new RuleError('Type a guess');
      const word = secretOf(privs).word;
      if (!word) throw new RuleError('No word yet');
      const verdict = judgeGuess(text, word);
      const name = nameOf(ctx, actor);
      if (verdict === 'correct') {
        const pts = guessPoints(ctx.now - g.drawStart, g.drawSec * 1000);
        const scores = g.scores.slice(), turnPts = g.turnPts.slice();
        scores[actor] = (scores[actor] || 0) + pts; turnPts[actor] = (turnPts[actor] || 0) + pts;
        if (ctx.seats[g.drawer]) { scores[g.drawer] = (scores[g.drawer] || 0) + DRAWER_PTS; turnPts[g.drawer] = (turnPts[g.drawer] || 0) + DRAWER_PTS; }
        let next: DrawState = { ...g, scores, turnPts, guessed: [...g.guessed, { seat: actor, at: ctx.now, pts }] };
        next = pushChat(next, { seat: actor, name, kind: 'correct', text: '' });
        const mine = { [actor]: { word, guessed: true } satisfies DrawPriv };
        if (allGuessed(next, ctx)) {
          const r = endTurn(next, privs, ctx, 'all');
          return { g: r.g, privs: r.privs, note: r.note };
        }
        return { g: next, privs: mine, note: `${name} guessed it!` };
      }
      if (verdict === 'close') {
        // Only the guesser learns it was close; the public state just ticks so their row is re-read.
        const prev = (privs[actor] as DrawPriv | null) ?? {};
        return { g: { ...g, chatN: g.chatN + 1 }, privs: { [actor]: { ...prev, close: text, closeN: g.chatN + 1 } satisfies DrawPriv } };
      }
      return { g: pushChat(g, { seat: actor, name, kind: 'guess', text }) };
    }
    throw new RuleError('Unknown move');
  },

  wakeAt: (g) => {
    if (g.phase === 'over') return null;
    if (g.phase === 'drawing' && g.hintTimes.length) return Math.min(g.endsAt, g.hintTimes[0]);
    return g.endsAt;
  },

  onWake: (g, privs, ctx) => {
    if (g.phase === 'choosing') {
      if (ctx.now < g.endsAt) return { g };
      const choices = (privs[g.drawer] as DrawPriv | null)?.choices;
      // The drawer didn't pick in time (or lost their row): the clock picks.
      const word = choices?.length ? choices[Math.floor(ctx.rng() * choices.length)] : pickChoices(g.difficulty, secretOf(privs).used, ctx.rng)[0];
      const r = startDrawing(g, privs, ctx, word);
      return { ...r, note: `${nameOf(ctx, g.drawer)} is drawing` };
    }
    if (g.phase === 'drawing') {
      if (ctx.now >= g.endsAt) return endTurn(g, privs, ctx, 'time');
      if (g.hintTimes.length && ctx.now >= g.hintTimes[0]) {
        const word = secretOf(privs).word;
        if (!word) return { g: { ...g, hintTimes: g.hintTimes.slice(1) } };
        const hidden = g.hint.map((c, i) => (c === '_' ? i : -1)).filter((i) => i >= 0);
        // Never give away the last hidden letter.
        if (hidden.length <= 1) return { g: { ...g, hintTimes: g.hintTimes.slice(1) } };
        const pick = hidden[Math.floor(ctx.rng() * hidden.length)];
        const hint = g.hint.slice(); hint[pick] = word[pick];
        return { g: { ...g, hint, hintTimes: g.hintTimes.slice(1) }, note: 'A letter was revealed' };
      }
      return { g };
    }
    if (g.phase === 'reveal') {
      if (ctx.now < g.endsAt) return { g };
      return advance(g, privs, ctx);
    }
    return { g };
  },

  onJoin: (g, _privs, seat, ctx) => {
    const scores = g.scores.slice(); scores[seat] = 0;
    const turnPts = g.turnPts.slice(); turnPts[seat] = 0;
    // A new player in a seat someone used earlier this round gets their own turn.
    const drawn = seat === g.drawer && g.phase !== 'reveal' ? g.drawn : g.drawn.filter((s) => s !== seat);
    const next = pushChat({ ...g, scores, turnPts, drawn, guessed: g.guessed.filter((x) => x.seat !== seat) },
      { seat: -1, name: '', kind: 'sys', text: `${nameOf(ctx, seat)} joined` });
    return { g: next, privs: { [seat]: null } };
  },

  onLeave: (g, privs, seat, ctx) => {
    if (g.phase === 'over') return { g };
    // The leaver is already gone from ctx.seats; drop their score so a newcomer starts at 0.
    const scores = g.scores.slice(); scores[seat] = 0;
    let next: DrawState = { ...g, scores, guessed: g.guessed.filter((x) => x.seat !== seat), drawn: g.drawn.filter((s) => s !== seat || s === g.drawer) };
    next = pushChat(next, { seat: -1, name: '', kind: 'sys', text: 'A player left' });
    if (seated(ctx).length < 2) return finish(next, privs, ctx);
    if (seat === g.drawer && (g.phase === 'choosing' || g.phase === 'drawing')) {
      return endTurn(next, privs, ctx, 'left', 'The drawer');
    }
    if (g.phase === 'drawing' && allGuessed(next, ctx)) return endTurn(next, privs, ctx, 'all');
    return { g: next };
  },

  isOver: (g) => g.phase === 'over',

  // Points-ranked: everyone's score; the top score (ties included) wins.
  results: (g, ctx) => seated(ctx).map((seat): SeatResult => ({
    seat,
    outcome: g.winners.includes(seat) ? 'win' : 'loss',
    points: g.scores[seat] || 0,
  })),
};
